// Shared application chrome: sidebar, top bar, navigation.
import { AI_EXERCISE_GENERATION_ENABLED } from "../config.js";
import { loadAccount, loadParentCenterData } from "../data/account.js";
import { avatarLabel, selectedAvatar } from "../domain/avatars.js";
import { learnerPresentation } from "../domain/learner.js";
import { t } from "../i18n.js";
import { app, escapeHTML } from "../lib/dom.js";
import { logout, render } from "../router.js";
import { state } from "../state.js";

export function navigation() {
  const items = state.teacherMode
    ? [
        ["home", "⌂", state.language === "it" ? "Panoramica" : "Overview"],
        ["classes", "🏫", state.language === "it" ? "Classi" : "Classes"],
        ["exercises", "✎", t("exercises")],
        AI_EXERCISE_GENERATION_ENABLED
          ? ["ai", "✨", "AI"]
          : ["ai", "＋", state.language === "it" ? "Crea" : "Create"],
        ["progress", "↗", t("progressNav")],
        ["account", "⚙", state.language === "it" ? "Account" : "Account"]
      ]
    : [
        ["home", "🗺️", state.language === "it" ? "Avventura" : "Adventure"],
        ["exercises", "📚", state.language === "it" ? "Compiti" : "Assignments"],
        ["progress", "🏆", state.language === "it" ? "Trofei" : "Trophies"]
      ];
  return items.map(([view, icon, label]) => `
    <button class="nav-button ${state.view === view ? "active" : ""}" data-view="${view}">
      <span class="nav-icon">${icon}</span><span class="nav-label">${label}</span>
    </button>`).join("");
}

function profileActionMenu(location, displayName, profileAvatar, shouldReturnToProfiles, languageTarget) {
  return `<div class="profile-actions ${location === "sidebar" ? "sidebar-profile-actions" : "topbar-profile-actions"}">
    <button class="profile-pill" data-profile-menu="${location}" aria-haspopup="menu" aria-expanded="false">
      <span class="avatar">${profileAvatar ? `<img class="profile-avatar-picture" src="${profileAvatar.image}" alt="${escapeHTML(avatarLabel(profileAvatar))}">` : escapeHTML(displayName[0].toUpperCase())}</span>
      <span class="profile-name">${escapeHTML(displayName)}</span>
      <span class="profile-chevron">⌄</span>
    </button>
    <div class="profile-action-menu" data-profile-action-menu="${location}" role="menu" hidden>
      <button type="button" data-profile-action="settings">⚙ ${state.language === "it" ? "Impostazioni account" : "Account settings"}</button>
      <div class="profile-language-switch" role="group" aria-label="${state.language === "it" ? "Lingua" : "Language"}"><span>🌍 ${state.language === "it" ? "Lingua" : "Language"}</span><button type="button" class="${state.language === "it" ? "active" : ""}" data-profile-language="it">🇮🇹 IT</button><button type="button" class="${state.language === "en" ? "active" : ""}" data-profile-language="en">🇬🇧 EN</button></div>
      <button type="button" data-profile-action="exit">↩ ${shouldReturnToProfiles ? (state.language === "it" ? "Torna al Centro Famiglia" : "Back to Family Center") : t("logout")}</button>
    </div>
  </div>`;
}

export function appLayout(content, title, subtitle) {
  const displayName = state.profile?.nickname || state.account.username;
  const profileAvatar = state.profile ? selectedAvatar() : null;
  const shouldReturnToProfiles = state.roles.includes("parent") && state.profile;
  const languageTarget = state.language === "it" ? "English" : "Italiano";
  const profileDetail = state.profile
    ? `${t("level")} ${state.profile.level} · ${state.profile.experience}/500 XP`
    : (state.language === "it" ? "Modalità insegnante" : "Teacher mode");
  const studentPresentation = state.profile && !state.teacherMode && !state.adminMode ? learnerPresentation() : null;
  const teacherPageIcons = { home: "▦", classes: "🏫", exercises: "✎", ai: "✦", progress: "↗", account: "⚙" };
  const shellClass = state.adminMode
    ? "admin-shell"
    : state.teacherMode
      ? "teacher-shell"
      : studentPresentation
        ? `student-shell student-${studentPresentation.mode}`
        : "";
  const studentPageIcons = { home: "🗺️", exercises: "📚", progress: "🏆", account: "⚙️" };
  app.innerHTML = `
    <div class="app-shell ${shellClass}">
      <aside class="sidebar">
        <div class="brand"><img class="brand-logo small" src="/brainmath-logo.png" alt="">BrainMath</div>
        ${profileActionMenu("sidebar", displayName, profileAvatar, shouldReturnToProfiles, languageTarget)}
        ${studentPresentation ? `<div class="student-world-badge"><span>${studentPresentation.symbol}</span><div><small>${state.language === "it" ? "Il tuo mondo" : "Your world"}</small><strong>${studentPresentation.title}</strong></div></div>` : ""}
        <nav class="nav-list">${navigation()}</nav>
        <div class="sidebar-footer">
          <div class="sidebar-profile">
            <strong>${escapeHTML(displayName)}</strong>
            <span>${profileDetail}</span>
          </div>
        </div>
      </aside>
      <main class="main-content">
        <header class="topbar ${studentPresentation ? "student-topbar" : state.teacherMode ? "teacher-topbar" : ""}">
          <div class="topbar-title-wrap">${studentPresentation ? `<span class="student-page-mark">${studentPageIcons[state.view] || "✨"}</span>` : state.teacherMode ? `<span class="professional-page-mark">${teacherPageIcons[state.view] || "▦"}</span>` : ""}<div>${state.teacherMode ? `<span class="workspace-eyebrow">${state.language === "it" ? "Area insegnante" : "Teacher workspace"}</span>` : ""}<h1>${title}</h1><p>${subtitle}</p></div></div>
          ${profileActionMenu("topbar", displayName, profileAvatar, shouldReturnToProfiles, languageTarget)}
        </header>
        ${content}
      </main>
      <nav class="mobile-nav">${navigation()}</nav>
    </div>`;
  document.querySelectorAll("[data-view]").forEach(button => {
    button.addEventListener("click", () => {
      state.view = button.dataset.view;
      render();
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  });
  document.querySelectorAll("[data-profile-menu]").forEach(profileButton => {
    const location = profileButton.dataset.profileMenu;
    const profileMenu = document.querySelector(`[data-profile-action-menu="${location}"]`);
    profileButton.addEventListener("click", event => {
      event.stopPropagation();
      const willOpen = profileMenu.hidden;
      document.querySelectorAll("[data-profile-action-menu]").forEach(menu => menu.hidden = true);
      document.querySelectorAll("[data-profile-menu]").forEach(button => button.setAttribute("aria-expanded", "false"));
      profileMenu.hidden = !willOpen;
      profileButton.setAttribute("aria-expanded", String(willOpen));
    });
  });
  document.querySelectorAll("[data-profile-action-menu]").forEach(profileMenu => {
    profileMenu.addEventListener("click", async event => {
      const selectedLanguage = event.target.closest("[data-profile-language]")?.dataset.profileLanguage;
      if (selectedLanguage) {
        event.stopPropagation();
        if (selectedLanguage === state.language) return;
        state.language = selectedLanguage;
        localStorage.setItem("brainmath-language", state.language);
        document.documentElement.lang = state.language;
        if (state.session) await loadAccount();
        render();
        return;
      }
      const action = event.target.closest("[data-profile-action]")?.dataset.profileAction;
      if (!action) return;
      document.querySelectorAll("[data-profile-action-menu]").forEach(menu => menu.hidden = true);
      document.querySelectorAll("[data-profile-menu]").forEach(button => button.setAttribute("aria-expanded", "false"));
      if (action === "settings") {
        state.view = "account";
        render();
        return;
      }
      if (shouldReturnToProfiles) {
        state.profile = null;
        state.parentMode = true;
        localStorage.removeItem("brainmath-profile");
        sessionStorage.removeItem("brainmath-parent-child-mode");
        await loadParentCenterData();
        render();
        return;
      }
      logout();
    });
  });
  if (window.__brainMathProfileMenuClose) {
    document.removeEventListener("click", window.__brainMathProfileMenuClose);
  }
  window.__brainMathProfileMenuClose = () => {
    document.querySelectorAll("[data-profile-action-menu]").forEach(menu => menu.hidden = true);
    document.querySelectorAll("[data-profile-menu]").forEach(button => button.setAttribute("aria-expanded", "false"));
  };
  document.addEventListener("click", window.__brainMathProfileMenuClose);
}

export function emptyExercises() {
  return `<div class="card empty-state">
    <div class="empty-icon">✓</div>
    <h2>${t("noExercises")}</h2>
    <p>${t("noExercisesHelp")}</p>
  </div>`;
}
